/** Unsubscriber class that usubscribes all his subscriptions on destroy */
import {
  ChangeDetectorRef,
  Component,
  forwardRef, inject,
  Injectable,
  Input,
  OnDestroy,
  Optional,
  Self
} from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {
  HttpClient,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {ApiWebListResults} from './_interfaces';

@Injectable()
export class Unsubscriber implements OnDestroy {
  protected readonly unsubscriber$: Subject<void> = new Subject();

  constructor() {
  }

  ngOnDestroy() {
    this.unsubscriber$.next();
    this.unsubscriber$.complete();
  }
}

@Component({
  template: '',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomValueAccessor),
      multi: true,
    },
  ],
})
export class CustomValueAccessor extends Unsubscriber implements ControlValueAccessor {
  @Input() isDisabled: boolean;

  value: any = '';

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    private cdr: ChangeDetectorRef
  ) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange = (_: any) => {
  };
  onTouched = () => {
  };

  writeValue(val: any): void {
    this.value = val;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onChangeHandler(value: any) {
    this.value = value;
    this.onChange(this.value);
  }
}

export abstract class ApiBaseService {

  protected client: HttpClient = inject(HttpClient);

  baseUrl: string;
  baseApiVersion: string;

  protected constructor(baseUrl: string, apiVersion: string = '') {
    this.baseUrl = baseUrl;
    this.baseApiVersion = apiVersion;
  }

  get$<T>(endpoint: string, params: any = {}, options: any = {}): Observable<T> {
    return this.client.get<T>(this.getCompleteUrl(endpoint), this.getOptions(params, options)).pipe(
      map((res: HttpResponse<T>) => res.body)
    );
  }

  getFullResponse$<T>(endpoint: string, params: any = {}, options: any = {}): Observable<HttpResponse<T>> {
    return this.client.get<T>(this.getCompleteUrl(endpoint), this.getOptions(params, options)).pipe(
      map((res: HttpResponse<T>) => res)
    );
  }

  getById$<T>(endpoint: string, id: number | string, options: any = {}): Observable<T> {
    return this.client.get<T>(this.getCompleteUrl(endpoint) + '/' + id, {...options, observe: 'response'}).pipe(
      map((res: HttpResponse<T>) => res.body)
    );
  }

  post$<T>(endpoint: string, item: any, options = {}): Observable<T> {
    return this.client.post<T>(this.getCompleteUrl(endpoint), item, {...options, observe: 'response'}).pipe(
      map((res: HttpResponse<T>) => res.body)
    );
  }

  put$<T>(endpoint: string, id: number | string, item: any, options = {}): Observable<T> {
    return this.client.put<T>(this.getCompleteUrl(endpoint) + '/' + id, item, {...options, observe: 'response'}).pipe(
      map((res: HttpResponse<T>) => res.body)
    );
  }

  patch$<T>(endpoint: string, id: number | string, item: any, options?): Observable<T> {
    return this.client.patch<T>(this.getCompleteUrl(endpoint) + '/' + id, item, {...options, observe: 'response'}).pipe(
      map((res: HttpResponse<T>) => res.body)
    );
  }

  delete$(endpoint: string, id: number | string, options?): Observable<ArrayBuffer> {
    return this.client.delete(this.getCompleteUrl(endpoint) + '/' + id, options);
  }

  // --------------------------------------------------------------------------------------------------------------------------

  private getOptions(params?: any, options: any = {}) {

    if (!('observe' in options)) {
      options = {...options, observe: 'response'};
    }

    if (params) {
      options.params = new HttpParams({
        fromObject: params
      });
    }

    return options;
  }

  private getApiString() {
    if (this.baseApiVersion) {
      return `${this.baseUrl}/${this.baseApiVersion}`;
    } else {
      return this.baseUrl;
    }
  }

  private getCompleteUrl(endpoint: string) {
    return `${this.getApiString()}/${endpoint}`;
  }
}

export class ApiWebService extends ApiBaseService {

  defaultEndpoint: string;

  /*E' possibile definire un endpoint di default per la risorsa che estende la classe*/
  constructor(endpoint?, apiUrl = environment.apiUrl, apiVersion = environment.apiVersion) {
    super(apiUrl, apiVersion);
    this.defaultEndpoint = (endpoint) ? endpoint : '';
  }

  override get$<T>(params: any = {}, endpoint: string = this.defaultEndpoint, options: any = {}): Observable<T> {
    return super.get$<T>(endpoint, params, options).pipe(
      map((res: T) => res)
    );
  }

  getById$<T>(id: number | string, endpoint: string = this.defaultEndpoint, options: any = {}): Observable<T> {
    return super.getById$<T>(endpoint, id, options);
  }

  getList$<T>(params: any, endpoint?: string, options: any = {}): Observable<ApiWebListResults<T>> {
    return super.getFullResponse$<T[]>(params, endpoint, options).pipe(
      map((res: HttpResponse<T[]>) => this.extractGetListResults<T>(res)),
      catchError(error => throwError(error))
    );
  }

  override post$<T>(item: any, endpoint: string = this.defaultEndpoint, options: any = {}): Observable<T> {
    return super.post$(endpoint, item, options);
  }

  override put$<T>(id: number | string, item: any, endpoint: string = this.defaultEndpoint, options: any = {}): Observable<T> {
    return super.put$<T>(endpoint, id, item, options);
  }

  override patch$<T>(id: number | string, item: any, endpoint: string = this.defaultEndpoint, options: any = {}): Observable<T> {
    return super.patch$<T>(endpoint, id, item, options);
  }

  override delete$(endpoint: string = this.defaultEndpoint, id: number | string, options: any = {}): Observable<ArrayBuffer> {
    return super.delete$(endpoint, id, options);
  }

  private extractGetListResults<T>(res: HttpResponse<T[]>): ApiWebListResults<T> {
    const totalItems = Number(res.headers.get('x-tot-items'));
    const totalFiltered = Number(res.headers.get('x-tot-filtered')) || totalItems;
    return {
      totalItems,
      totalFiltered,
      items: res.body
    };
  }

}
